/**
 * Bot trades page — unified entry+exit paired view across all six trading agents.
 *
 * Pulls from /api/bot-trades which reads scanner/trades.csv and enriches open
 * positions with current price from Polygon. Filter / sort / paginate client-side
 * by re-querying with params. Auto-refreshes every 60s while page is open.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Badge, Card, Col, Form, Row, Table, Button, Spinner, Modal,
} from "react-bootstrap";
import toast from "react-hot-toast";

import { getBotTrades, saveBotTradeNote } from "../services/botTrades.service";
import ErrorPage from "../ui/ErrorPage.component";
import { lookupStrategyMeta } from "./strategyMeta";
import { useUser } from "../hooks/useUser";

// ---- formatting ----

function fmtDollar(x, opts = {}) {
  if (x === null || x === undefined || Number.isNaN(x)) return "—";
  const v = Math.abs(x).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const sign = x >= 0 ? "+" : "−";
  return (opts.unsigned ? "$" + v : sign + "$" + v);
}
function fmtPct(x) {
  if (x === null || x === undefined || Number.isNaN(x)) return "—";
  return (x >= 0 ? "+" : "") + x.toFixed(2) + "%";
}
function fmtPx(x) {
  if (x === null || x === undefined || Number.isNaN(x)) return "—";
  return "$" + Number(x).toFixed(2);
}
function fmtR(x) {
  if (x === null || x === undefined || Number.isNaN(x)) return "—";
  return (x >= 0 ? "+" : "") + Number(x).toFixed(2) + "R";
}
function fmtDateET(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
      hour12: false,
    });
  } catch { return iso; }
}
function fmtHold(ms) {
  if (ms === null || ms === undefined) return "—";
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (days >= 1) return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function pnlClass(x) {
  if (x === null || x === undefined || Number.isNaN(x)) return "";
  return x > 0 ? "text-success" : x < 0 ? "text-danger" : "";
}

// ---- strategy info modal ----

function StrategyInfoModal({ show, onHide, meta }) {
  // ``meta`` may be null if the user opened the modal for a bot we
  // don't have a card for yet — render a placeholder rather than
  // blanking the modal so the click stays explicable.
  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex flex-column">
          <span>{meta?.title || "Strategy reference"}</span>
          {meta && (
            <small className="text-muted">
              <code>{meta.bot}</code> · strategy tag <code>{meta.strategy_tag}</code>
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!meta && (
          <Alert variant="warning" className="mb-0">
            No reference card yet for this bot. Add one to{" "}
            <code>frontend/src/components/strategyMeta.js</code>.
          </Alert>
        )}
        {meta && (
          <>
            <p className="lead mb-3">{meta.one_liner}</p>

            <Row className="g-3 mb-3">
              <Col md={4}><small className="text-muted d-block">Timeframe</small><strong>{meta.timeframe}</strong></Col>
              <Col md={4}><small className="text-muted d-block">Universe</small><strong>{meta.universe}</strong></Col>
              <Col md={4}><small className="text-muted d-block">Account</small><strong>{meta.account}</strong></Col>
            </Row>

            <h6 className="text-muted text-uppercase mt-3 mb-2" style={{ fontSize: "0.75rem" }}>Entry / Stop / Target</h6>
            <Table size="sm" borderless className="mb-3">
              <tbody>
                <tr><td style={{ width: 110 }}><small className="text-muted">Entry</small></td><td>{meta.entry}</td></tr>
                <tr><td><small className="text-muted">Stop</small></td><td className="text-danger">{meta.stop}</td></tr>
                <tr><td><small className="text-muted">Target</small></td><td className="text-success">{meta.target}</td></tr>
                <tr><td><small className="text-muted">Risk</small></td><td>{meta.risk}</td></tr>
                {meta.kill_switch && (
                  <tr><td><small className="text-muted">Kill switch</small></td><td><small className="text-muted">{meta.kill_switch}</small></td></tr>
                )}
              </tbody>
            </Table>

            <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>Exit paths</h6>
            <ul className="mb-3">
              {meta.exit_paths.map((p) => <li key={p}>{p}</li>)}
            </ul>

            <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>Signal filters</h6>
            <ul className="mb-3">
              {meta.filters.map((f) => <li key={f}><small>{f}</small></li>)}
            </ul>

            <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>Backtest results</h6>
            <Card className="mb-3" body>
              <Row className="g-3 mb-2">
                <Col md={6} lg={3}><small className="text-muted d-block">Window</small><strong>{meta.backtest.window}</strong></Col>
                <Col md={6} lg={3}><small className="text-muted d-block">Win rate</small><strong>{meta.backtest.win_rate}</strong></Col>
                <Col md={6} lg={3}><small className="text-muted d-block">Expectancy</small><strong>{meta.backtest.expectancy}</strong></Col>
                <Col md={6} lg={3}><small className="text-muted d-block">Profit factor</small><strong>{meta.backtest.profit_factor}</strong></Col>
                <Col xs={12}><small className="text-muted d-block">CAGR</small><strong>{meta.backtest.cagr || "—"}</strong></Col>
              </Row>
              <small className="text-muted">{meta.backtest.headline}</small>
            </Card>

            <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>Why we deployed this</h6>
            <ul className="mb-3">
              {meta.why_deployed.map((r) => <li key={r}>{r}</li>)}
            </ul>

            {meta.references && meta.references.length > 0 && (
              <>
                <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>References</h6>
                <ul className="mb-0">
                  {meta.references.map((r) => (
                    <li key={r}><code><small>{r}</small></code></li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// Small clickable wrapper for the bot/strategy cell text. Renders as a
// link-styled button so screen readers + keyboard nav work, but keeps
// the bot column visually compact.
function StrategyLink({ children, onClick, title }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="btn btn-link p-0 align-baseline"
      style={{ textDecoration: "underline dotted", textUnderlineOffset: "2px" }}
      title={title || "What is this strategy?"}
    >
      {children}
    </button>
  );
}

// ---- summary card ----

function SummaryCard({ summary, generatedAt }) {
  if (!summary) return null;
  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4 className="mb-0">Bot Trades</h4>
          <small className="text-muted">
            {generatedAt && new Date(generatedAt).toLocaleString()} · auto-refresh 60s
          </small>
        </div>
        <Row>
          <Col xs={6} md={2}>
            <small className="text-muted">Total trades</small>
            <div><strong>{summary.n_total}</strong> ({summary.n_closed} closed · {summary.n_open} open)</div>
          </Col>
          <Col xs={6} md={2}>
            <small className="text-muted">Win rate</small>
            <div>
              <strong>{summary.win_rate_pct === null ? "—" : summary.win_rate_pct.toFixed(1) + "%"}</strong>{" "}
              <small className="text-muted">({summary.n_wins}W / {summary.n_losses}L)</small>
            </div>
          </Col>
          <Col xs={6} md={2}>
            <small className="text-muted">Realized P&amp;L</small>
            <div className={pnlClass(summary.realized_pnl)}>
              <strong>{fmtDollar(summary.realized_pnl)}</strong>
            </div>
          </Col>
          <Col xs={6} md={2}>
            <small className="text-muted">Unrealized P&amp;L</small>
            <div className={pnlClass(summary.unrealized_pnl)}>
              <strong>{fmtDollar(summary.unrealized_pnl)}</strong>
            </div>
          </Col>
          <Col xs={6} md={2}>
            <small className="text-muted">Avg R</small>
            <div className={pnlClass(summary.avg_r)}>
              <strong>{fmtR(summary.avg_r)}</strong>
            </div>
          </Col>
          <Col xs={6} md={2}>
            <small className="text-muted">Avg win / loss</small>
            <div>
              <span className="text-success">{fmtDollar(summary.avg_win)}</span>{" / "}
              <span className="text-danger">{fmtDollar(summary.avg_loss)}</span>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

// ---- per-bot breakdown ----

function ByBotCard({ byBot, activeBot, onPick, onInfo }) {
  if (!byBot || byBot.length === 0) return null;
  return (
    <Card className="mb-3">
      <Card.Body className="p-0">
        <Table responsive hover size="sm" className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Bot</th>
              <th className="text-end">Trades</th>
              <th className="text-end">Win rate</th>
              <th className="text-end">Realized</th>
              <th className="text-end">Unrealized</th>
              <th className="text-end">Total P&amp;L</th>
              <th className="text-end">Avg R</th>
              <th className="text-end">Avg win / loss</th>
            </tr>
          </thead>
          <tbody>
            {byBot.map((b) => {
              const isActive = activeBot === b.bot;
              return (
                <tr
                  key={b.bot}
                  onClick={() => onPick(isActive ? "" : b.bot)}
                  style={{ cursor: "pointer" }}
                  className={isActive ? "table-active" : ""}
                >
                  <td>
                    <StrategyLink onClick={() => onInfo({ bot: b.bot })}>
                      <code>{b.bot}</code>
                    </StrategyLink>
                  </td>
                  <td className="text-end">
                    <strong>{b.n_total}</strong>{" "}
                    <small className="text-muted">({b.n_closed}c · {b.n_open}o)</small>
                  </td>
                  <td className="text-end">
                    {b.win_rate_pct === null ? "—" : b.win_rate_pct.toFixed(1) + "%"}{" "}
                    <small className="text-muted">({b.n_wins}/{b.n_losses})</small>
                  </td>
                  <td className={"text-end " + pnlClass(b.realized_pnl)}>{fmtDollar(b.realized_pnl)}</td>
                  <td className={"text-end " + pnlClass(b.unrealized_pnl)}>{fmtDollar(b.unrealized_pnl)}</td>
                  <td className={"text-end " + pnlClass(b.total_pnl)}><strong>{fmtDollar(b.total_pnl)}</strong></td>
                  <td className={"text-end " + pnlClass(b.avg_r)}>{fmtR(b.avg_r)}</td>
                  <td className="text-end">
                    <span className="text-success">{fmtDollar(b.avg_win)}</span>
                    {" / "}
                    <span className="text-danger">{fmtDollar(b.avg_loss)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

// ---- filter bar ----

function FilterBar({ filters, value, onChange, onReset }) {
  const set = (k, v) => onChange({ ...value, [k]: v, offset: 0 });
  return (
    <Card className="mb-3">
      <Card.Body className="py-2">
        <Row className="g-2 align-items-end">
          <Col xs={6} md={2}>
            <Form.Label className="small mb-0">Bot</Form.Label>
            <Form.Select size="sm" value={value.bot || ""} onChange={(e) => set("bot", e.target.value)}>
              <option value="">All bots</option>
              {(filters?.bots || []).map((b) => <option key={b} value={b}>{b}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-0">Strategy</Form.Label>
            <Form.Select size="sm" value={value.strategy || ""} onChange={(e) => set("strategy", e.target.value)}>
              <option value="">All strategies</option>
              {(filters?.strategies || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-0">Account</Form.Label>
            <Form.Select size="sm" value={value.account || ""} onChange={(e) => set("account", e.target.value)}>
              <option value="">All accounts</option>
              {(filters?.accounts || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-0">Status</Form.Label>
            <Form.Select size="sm" value={value.status || ""} onChange={(e) => set("status", e.target.value)}>
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-0">Result</Form.Label>
            <Form.Select size="sm" value={value.result || ""} onChange={(e) => set("result", e.target.value)}>
              <option value="">All</option>
              <option value="win">Wins</option>
              <option value="loss">Losses</option>
              <option value="breakeven">Breakeven</option>
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-0">Ticker</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              placeholder="e.g. XEL"
              value={value.ticker || ""}
              onChange={(e) => set("ticker", e.target.value)}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-0">From</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={value.from || ""}
              onChange={(e) => set("from", e.target.value)}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-0">To</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={value.to || ""}
              onChange={(e) => set("to", e.target.value)}
            />
          </Col>
          <Col xs={6} md={2}>
            <Button variant="outline-secondary" size="sm" onClick={onReset} className="w-100">
              Reset
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

// ---- trades table ----

const COLUMNS = [
  { key: "ticker",     label: "Ticker"   },
  { key: "bot",        label: "Bot"      },
  { key: "strategy",   label: "Strategy" },
  { key: "status",     label: "Status"   },
  { key: "entry_ts",   label: "Entered"  },
  { key: "entry_px",   label: "Entry"    },
  { key: "stop_px",    label: "Stop"     },
  { key: "target_px",  label: "Target"   },
  { key: "qty",        label: "Qty"      },
  { key: "exit_ts",    label: "Exited"   },
  { key: "exit_px",    label: "Exit"     },
  { key: "pnl_dollar", label: "P&L $"   },
  { key: "pnl_pct",    label: "P&L %"   },
  { key: "r_mult",     label: "R"        },
  { key: "hold_ms",    label: "Hold"     },
  { key: "exit_reason",label: "Reason"   },
  { key: "note",       label: "Notes", sortable: false },
];

function SortHeader({ col, sortBy, sortDir, onClick }) {
  const active = col.key === sortBy;
  const sortable = col.sortable !== false;
  if (!sortable) {
    return <th style={{ whiteSpace: "nowrap" }}>{col.label}</th>;
  }
  return (
    <th onClick={() => onClick(col.key)} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
      {col.label}
      {active && (sortDir === "asc" ? " ▲" : " ▼")}
    </th>
  );
}

function NotesCell({ tradeKey, note, isSaving, onSave, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note || "");
  const textareaRef = useRef(null);

  // Re-sync when the prop changes from a parent refetch.
  useEffect(() => {
    if (!editing) setDraft(note || "");
  }, [note, editing]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [editing]);

  if (!tradeKey) {
    return <td><small className="text-muted">—</small></td>;
  }

  // Viewers can read notes but not write them — the PUT is admin-only, so
  // don't hand them an editor that will 403 on blur.
  if (readOnly) {
    return (
      <td style={{ minWidth: 160, maxWidth: 320 }} title={note || ""}>
        {note ? (
          <small style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{note}</small>
        ) : (
          <small className="text-muted">—</small>
        )}
      </td>
    );
  }

  const commit = () => {
    const next = draft.trim();
    if (next !== (note || "")) onSave(tradeKey, next);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(note || "");
    setEditing(false);
  };

  if (editing) {
    return (
      <td style={{ minWidth: 200 }}>
        <Form.Control
          as="textarea"
          ref={textareaRef}
          size="sm"
          rows={2}
          value={draft}
          disabled={isSaving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
        />
      </td>
    );
  }

  return (
    <td
      style={{ minWidth: 160, maxWidth: 320, cursor: "pointer" }}
      onClick={() => setEditing(true)}
      title={note || "Click to add a note"}
    >
      {note ? (
        <small style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{note}</small>
      ) : (
        <small className="text-muted fst-italic">+ note</small>
      )}
    </td>
  );
}

function TradeRow({ t, onSaveNote, savingKey, onInfo, notesReadOnly }) {
  return (
    <tr>
      <td><strong>{t.ticker}</strong></td>
      <td>
        <StrategyLink onClick={() => onInfo({ bot: t.bot, strategy: t.strategy })}>
          <code>{t.bot}</code>
        </StrategyLink>
      </td>
      <td>
        <StrategyLink onClick={() => onInfo({ bot: t.bot, strategy: t.strategy })}>
          <small>{t.strategy}</small>
        </StrategyLink>
      </td>
      <td>
        {t.status === "open"
          ? <Badge bg="info">Open</Badge>
          : t.pnl_dollar > 0
            ? <Badge bg="success">Win</Badge>
            : t.pnl_dollar < 0
              ? <Badge bg="danger">Loss</Badge>
              : <Badge bg="secondary">BE</Badge>
        }
      </td>
      <td><small>{fmtDateET(t.entry_ts)}</small></td>
      <td className="text-end">{fmtPx(t.entry_px)}</td>
      <td className="text-end text-danger">{fmtPx(t.stop_px)}</td>
      <td className="text-end text-success">{fmtPx(t.target_px)}</td>
      <td className="text-end">{t.qty ?? "—"}</td>
      <td><small>{t.status === "open" ? "—" : fmtDateET(t.exit_ts)}</small></td>
      <td className="text-end">
        {t.status === "open"
          ? <span className="text-muted">{fmtPx(t.current_px)}</span>
          : fmtPx(t.exit_px)}
      </td>
      <td className={"text-end " + pnlClass(t.pnl_dollar)}>{fmtDollar(t.pnl_dollar)}</td>
      <td className={"text-end " + pnlClass(t.pnl_pct)}>{fmtPct(t.pnl_pct)}</td>
      <td className={"text-end " + pnlClass(t.r_mult)}>{fmtR(t.r_mult)}</td>
      <td><small>{fmtHold(t.hold_ms)}</small></td>
      <td><small className="text-muted">{t.exit_reason || "—"}</small></td>
      <NotesCell
        tradeKey={t.trade_key}
        note={t.note}
        isSaving={savingKey === t.trade_key}
        onSave={onSaveNote}
        readOnly={notesReadOnly}
      />
    </tr>
  );
}

// ---- top-level page ----

const DEFAULT_QUERY = { sort_by: "entry_ts", sort_dir: "desc", limit: 100, offset: 0 };

export default function BotTrades() {
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [infoTarget, setInfoTarget] = useState(null); // { bot, strategy } | null
  const queryClient = useQueryClient();
  const { isAdminAuthenticated } = useUser();

  const infoMeta = useMemo(
    () => (infoTarget ? lookupStrategyMeta(infoTarget) : null),
    [infoTarget]
  );

  // Strip empty params before sending to the API so the query key is stable.
  const apiParams = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(query)) {
      if (v !== "" && v !== null && v !== undefined) out[k] = v;
    }
    return out;
  }, [query]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["botTrades", apiParams],
    queryFn: () => getBotTrades(apiParams),
    refetchInterval: 60_000,
    keepPreviousData: true,
  });

  const noteMutation = useMutation({
    mutationFn: ({ tradeKey, note }) => saveBotTradeNote(tradeKey, note),
    onSuccess: (saved) => {
      // Optimistic-ish: patch the cached page in place so the row updates
      // without waiting for the next refetch.
      queryClient.setQueryData(["botTrades", apiParams], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          trades: prev.trades.map((t) =>
            t.trade_key === saved.tradeKey ? { ...t, note: saved.note } : t
          ),
        };
      });
      toast.success(saved.deleted ? "Note cleared" : "Note saved");
    },
    onError: (err) => toast.error(err.message || "Failed to save note"),
  });

  const onSaveNote = (tradeKey, note) =>
    noteMutation.mutate({ tradeKey, note });
  const savingKey = noteMutation.isLoading ? noteMutation.variables?.tradeKey : null;

  if (isLoading && !data) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" /> <span className="ms-2">Loading bot trades…</span>
      </div>
    );
  }
  if (error) return <ErrorPage errorCode="500" description={error.message || "Failed to load"} />;

  const filters = data?.filters || {};
  const trades = data?.trades || [];
  const total = data?.total || 0;
  const summary = data?.summary;
  const csvMissing = data && data.csv_present === false;

  const onSort = (key) => {
    const dir = query.sort_by === key && query.sort_dir === "desc" ? "asc" : "desc";
    setQuery({ ...query, sort_by: key, sort_dir: dir });
  };
  const onReset = () => setQuery(DEFAULT_QUERY);
  const onPage = (delta) => setQuery({ ...query, offset: Math.max(0, (query.offset || 0) + delta) });

  return (
    <div className="container-fluid py-3">
      {csvMissing && (
        <Alert variant="warning">
          No <code>trades.csv</code> found at <code>{data.csv_path}</code> — bots haven't logged any trades yet,
          or this server isn't co-located with the agents.
        </Alert>
      )}

      <SummaryCard summary={summary} generatedAt={data?.generated_at} />
      <ByBotCard
        byBot={summary?.by_bot}
        activeBot={query.bot || ""}
        onPick={(bot) => setQuery({ ...query, bot, offset: 0 })}
        onInfo={setInfoTarget}
      />
      <FilterBar filters={filters} value={query} onChange={setQuery} onReset={onReset} />

      <StrategyInfoModal
        show={!!infoTarget}
        onHide={() => setInfoTarget(null)}
        meta={infoMeta}
      />

      <Card>
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="mb-0">
            <thead className="table-light">
              <tr>
                {COLUMNS.map((c) => (
                  <SortHeader
                    key={c.key} col={c}
                    sortBy={query.sort_by} sortDir={query.sort_dir}
                    onClick={onSort}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 && (
                <tr><td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                  No trades match the current filters.
                </td></tr>
              )}
              {trades.map((t, i) => (
                <TradeRow
                  key={`${t.entry_order_id || t.exit_order_id}-${i}`}
                  t={t}
                  onSaveNote={onSaveNote}
                  savingKey={savingKey}
                  onInfo={setInfoTarget}
                  notesReadOnly={!isAdminAuthenticated}
                />
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <small className="text-muted">
          Showing {trades.length} of {total} trade{total === 1 ? "" : "s"}
          {query.offset > 0 && <> · offset {query.offset}</>}
        </small>
        <div>
          <Button
            size="sm" variant="outline-secondary"
            onClick={() => onPage(-query.limit)}
            disabled={(query.offset || 0) === 0}
            className="me-2"
          >
            ← Prev
          </Button>
          <Button
            size="sm" variant="outline-secondary"
            onClick={() => onPage(query.limit)}
            disabled={(query.offset || 0) + (query.limit || 100) >= total}
          >
            Next →
          </Button>
        </div>
      </div>
    </div>
  );
}
